import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Route, Switch, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  loadMoreSearchResults,
  refreshAudience,
  CS_POST_LOAD_MORE_SEARCH_RESULTS_SUCCESS,
  clearAllSelectedUsers,
  getSettings,
} from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/CreatorSearch/redux/creatorSearchActions";
import METHODS from "src/app/pages/CreatorDiscoveryTool/legacy/app/helpers/methods";
import CONSTS from "src/app/pages/CreatorDiscoveryTool/legacy/app/helpers/consts";
import CreatorSearchToolbar from "./components/CreatorSearchToolbar";
import CreatorSearchFilters from "./components/CreatorSearchFilters/CreatorSearchFilters";
import CreatorResults from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/CreatorResults/CreatorResults";
import CreatorSearchLabels from "../components/SearchLabels/containers/CreatorSearchLabels";
import { ProfileDataPane } from "src/app/pages/CreatorDiscoveryTool/legacy/app/profile/ProfileDataPane/ProfileDataPane";
import {
  getInitialFilters,
  mapSortOptionsFromBackend,
  getQueryDataObject,
  hasOnlyCreatorSearchTabParam,
  handleMapFilters,
  areFiltersEmpty,
  handleSaveFiltersToDB,
} from "./utils";
import AnalyzeBriefLoader from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/components/AnalyzeBriefLoader";
import ProjectBriefPreview from "../components/ProjectBriefPreview";
import CreateProjectButton from "../components/CreateProjectButton";
import HorizontalScroller from "src/app/components/Tabs/components/HorizontalScroller";
import useSearchCreators from "./hooks/useSearchCreators";
import useGetCreatorSearchFiltersData from "./hooks/useGetCreatorSearchFiltersData";
import useElementHeight from "./hooks/useElementHeight";
import { usePageTitle } from "src/app/title-management/usePageTitle";
import CreatorSearchBar from "./components/CreatorSearchBar";
import CreatorSearchPanes from "./components/CreatorSearchPanes";
import isRecord from "src/utils/isRecord";
import {
  setIsMatchingFilters,
  setSearchHistoryPaneOpen,
} from "src/redux/project/projectActions";
import "./components/CreatorSearchSortColumns.scss";

export default function CreatorSearchView({
  renderTopBar,
  singleProjectRef,
  isActivateProjectPaneOpen,
  handleCloseActivateProjectPane,
  handleOpenActivateProjectPane,
  isResearchProject,
}) {
  usePageTitle("Creator Search");

  const [searchFilters, setSearchFilters] = useState(getInitialFilters);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [suggestedFilters, setSuggestedFilters] = useState(getInitialFilters);
  const [sortOrder, setSortOrder] = useState("desc");

  const hasInitializedSavedFiltes = useRef(false);
  const intl = useIntl();

  const rawSortOptions = useSelector(
    (state) => state.creatorSearchReducer.sortOptions,
  );

  const sortOptions = mapSortOptionsFromBackend(rawSortOptions, intl);

  const [sortBy, setSortBy] = useState(null);
  const hasInitializedSortBy = useRef(false);

  const [labelsRef, labelsHeight] = useElementHeight();

  const { workspaceUuid, projectId } = useParams();
  const dispatch = useDispatch();

  const searchAudienceResult = useSelector(
    (state) => state.creatorSearchReducer.searchAudienceResult,
  );
  const searchAudienceInfo = useSelector(
    (state) => state.creatorSearchReducer.searchAudienceInfo,
  );

  const isAudienceDataError = useSelector(
    (state) => state.creatorSearchReducer.isAudienceDataError,
  );

  const lastFetchWasEmpty = useSelector(
    (state) => state.creatorSearchReducer.lastFetchWasEmpty,
  );

  const audienceCountries = useSelector(
    (state) => state.creatorSearchReducer.audienceCountries,
  );

  const audienceLanguages = useSelector(
    (state) => state.creatorSearchReducer.audienceLanguages,
  );

  const influencerCountries = useSelector(
    (state) => state.creatorSearchReducer.influencerCountries,
  );

  const creatorTags = useSelector(
    (state) => state.creatorSearchReducer.creatorTags,
  );

  const projectLoading = useSelector(
    (state) => state.projectReducer.projectLoading,
  );

  const { savedFilters, filtersMetaFieldUuid } =
    useGetCreatorSearchFiltersData();

  const normalizedSavedFilters = useMemo(() => {
    if (!savedFilters) {
      return null;
    }

    try {
      const parsed = JSON.parse(savedFilters);
      return isRecord(parsed) ? parsed : null;
    } catch (error) {
      console.error("Error parsing saved filters; ", error);
      return null;
    }
  }, [savedFilters]);

  const isMatchingFilters = useSelector(
    (state) => state.projectReducer.isMatchingFilters,
  );

  const searchHistoryPaneState = useSelector(
    (state) => state.projectReducer.searchHistoryPaneState,
  );

  useSearchCreators(searchFilters, sortBy, sortOrder, projectId);

  useEffect(() => {
    setPage(1);
  }, [searchFilters, sortBy, sortOrder]);

  const endOfList =
    isAudienceDataError ||
    lastFetchWasEmpty ||
    !METHODS.hasMoreResults(
      page,
      CONSTS.audienceResultsPageSize,
      searchAudienceInfo?.totalResults,
    );

  const getMoreResults = async (endOfList) => {
    if (listLoading || endOfList) return;

    setListLoading(true);

    const newPage = page + 1;
    const excludedUserIds = searchAudienceResult.flatMap((result) => [
      result.userId,
      ...Object.values(result.socialProviders || {}).map((provider) => provider.userId),
    ].filter(Boolean));
    const queryData = getQueryDataObject(
      searchFilters,
      newPage,
      sortBy,
      sortOrder,
      excludedUserIds,
    );

    dispatch(loadMoreSearchResults(queryData)).then((r) => {
      if (r.type === CS_POST_LOAD_MORE_SEARCH_RESULTS_SUCCESS) {
        setPage(newPage);
      }
      setListLoading(false);
    });
  };

  const clearAllSelected = () => {
    dispatch(clearAllSelectedUsers());
  };

  const refreshListData = async () => {
    dispatch(refreshAudience(searchFilters, page, sortBy, sortOrder));
  };

  const handleSortByChange = (nextSortBy) => {
    setSortBy(nextSortBy);

    if (!hasInitializedSortBy.current || !nextSortBy) {
      return;
    }

    handleSaveFiltersToDB(
      filtersMetaFieldUuid,
      searchFilters,
      suggestedFilters,
      projectId,
      nextSortBy,
      sortOrder,
    );
  };

  const handleSortOrderChange = (nextSortOrder) => {
    setSortOrder(nextSortOrder);

    if (!hasInitializedSortBy.current || !sortBy) {
      return;
    }

    handleSaveFiltersToDB(
      filtersMetaFieldUuid,
      searchFilters,
      suggestedFilters,
      projectId,
      sortBy,
      nextSortOrder,
    );
  };

  const changeBaseFilters = (newFilters, shouldResetSuggestedFilters) => {
    setSearchFilters(newFilters);
    if (shouldResetSuggestedFilters) {
      setSuggestedFilters(getInitialFilters());
    }

    handleSaveFiltersToDB(
      filtersMetaFieldUuid,
      newFilters,
      shouldResetSuggestedFilters ? null : suggestedFilters,
      projectId,
      sortBy,
      sortOrder,
    );
  };

  const handleUpdateBothFilters = (newBaseFilters, newSuggestedFilters) => {
    setSearchFilters(newBaseFilters);
    setSuggestedFilters(newSuggestedFilters);

    handleSaveFiltersToDB(
      filtersMetaFieldUuid,
      newBaseFilters,
      newSuggestedFilters,
      projectId,
      sortBy,
      sortOrder,
    );
  };

  const resetFiltersToDefault = () => {
    setSearchFilters(getInitialFilters());
    setSuggestedFilters(getInitialFilters());
  };

  const clearFilters = () => {
    resetFiltersToDefault();

    handleSaveFiltersToDB(filtersMetaFieldUuid, null, null, projectId);

    setTimeout(() => {
      toggleFilters();
    }, 50);
  };

  const toggleFilters = () => {
    setFiltersVisible((prev) => !prev);
  };

  useEffect(() => {
    dispatch(getSettings(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (projectLoading) return;
    // Prevent updates from websocket
    if (hasInitializedSavedFiltes.current) return;
    hasInitializedSavedFiltes.current = true;

    if (
      !normalizedSavedFilters &&
      !projectLoading &&
      !window.location.pathname.includes("profile") &&
      hasOnlyCreatorSearchTabParam()
    ) {
      setFiltersVisible(true);
    }
  }, [normalizedSavedFilters, projectLoading]);

  useEffect(() => {
    if (!normalizedSavedFilters) {
      return;
    }

    dispatch(setIsMatchingFilters(false));

    const mappedBaseFilters = handleMapFilters(
      normalizedSavedFilters,
            audienceCountries,
            influencerCountries,
            creatorTags,
            audienceLanguages,
          );if (mappedBaseFilters) {
      setSearchFilters(mappedBaseFilters);
    }

    const mappedSuggestedFilters = normalizedSavedFilters?.aiSuggestions
      ? handleMapFilters(
          normalizedSavedFilters.aiSuggestions,
            audienceCountries,
            influencerCountries,
            creatorTags,
            audienceLanguages,
          )
      : getInitialFilters();

    if (mappedSuggestedFilters) {
      setSuggestedFilters(mappedSuggestedFilters);
    }
  }, [
    normalizedSavedFilters,
    audienceCountries,
    audienceLanguages,
    influencerCountries,
    creatorTags,
    dispatch,
  ]);

  useEffect(() => {
    if (!sortOptions.length || hasInitializedSortBy.current) {
      return;
    }

    const isValidSortOption = (sortValue) =>
      Boolean(sortValue) &&
      sortOptions.some((option) => option.uuid === sortValue);

    const savedSortBy = normalizedSavedFilters?.sortBy;
    const savedSortOrder = normalizedSavedFilters?.sortOrder;

    if (isValidSortOption(savedSortBy)) {
      setSortBy(savedSortBy);
    } else {
      setSortBy(sortOptions[0].uuid);
    }

    if (savedSortOrder === "asc" || savedSortOrder === "desc") {
      setSortOrder(savedSortOrder);
    }

    hasInitializedSortBy.current = true;
  }, [sortOptions, normalizedSavedFilters]);

  useEffect(() => {
    return () => {
      dispatch(clearAllSelectedUsers());
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      searchHistoryPaneState.isOpen &&
      searchHistoryPaneState.projectId !== projectId
    ) {
      dispatch(setSearchHistoryPaneOpen(true, projectId));
    }
  }, [
    isResearchProject,
    projectId,
    searchHistoryPaneState.isOpen,
    searchHistoryPaneState.projectId,
    dispatch,
  ]);

  return (
    <>
      {renderTopBar(
        <CreateProjectButton
          handleOpenCreateNewProjectPane={handleOpenActivateProjectPane}
        />,
      )}
      <div className="single-project__toolbar-wrapper">
        <HorizontalScroller>
          <ProjectBriefPreview />
          <CreatorSearchToolbar
            sortBy={sortBy}
            setSortBy={handleSortByChange}
            openFilters={() => setFiltersVisible(true)}
            clearFilters={clearFilters}
            resetFiltersToDefault={resetFiltersToDefault}
            areFiltersEmpty={
              areFiltersEmpty(searchFilters) && areFiltersEmpty(suggestedFilters)
            }
            projectId={projectId}
            displaySearchHistoryButton={isResearchProject}
            sortOptions={sortOptions}
          />
        </HorizontalScroller>
      </div>
      <div className="audience-search__labels-wrapper" ref={labelsRef}>
        <CreatorSearchLabels
          searchFilters={searchFilters}
          changeFilters={changeBaseFilters}
          suggestedFilters={suggestedFilters}
          updateBothFilters={handleUpdateBothFilters}
        />
      </div>

      {isMatchingFilters ? (
        <AnalyzeBriefLoader />
      ) : (
        <CreatorResults
          searchData={searchFilters}
          getListData={refreshListData}
          userList={searchAudienceResult}
          getMoreResults={getMoreResults}
          listLoading={listLoading}
          toggleFilters={toggleFilters}
          endOfList={endOfList}
          getScrollParent={() => singleProjectRef.current}
          labelsHeight={labelsHeight}
          sortBy={sortBy}
          setSortBy={handleSortByChange}
          sortOrder={sortOrder}
          setSortOrder={handleSortOrderChange}
        />
      )}
      {filtersVisible && (
        <CreatorSearchFilters
          closeFilters={() => setFiltersVisible(false)}
          filters={searchFilters}
          applyFilters={changeBaseFilters}
        />
      )}
      <CreatorSearchBar
        clearAllSelected={clearAllSelected}
        projectId={projectId}
        workspaceUuid={workspaceUuid}
        handleOpenActivateProjectPane={handleOpenActivateProjectPane}
      />
      <Switch>
        <Route
          path={[
            `/workspace/${workspaceUuid}/projects/${projectId}/profile/:socialProvider/:socialUsername`,
          ]}
          render={(props) => {
            return (
              <ProfileDataPane
                {...props}
                key={props.match.params.socialUsername}
                refreshAudience={refreshListData}
                searchFilters={searchFilters}
                projectId={projectId}
              />
            );
          }}
        />
      </Switch>
      <CreatorSearchPanes
        isActivateProjectPaneOpen={isActivateProjectPaneOpen}
        projectId={projectId}
        handleCloseActivateProjectPane={handleCloseActivateProjectPane}
      />
    </>
  );
}
